import { Box, Text, Spinner, Alert, AlertIcon, Button, Flex, Textarea, Grid, GridItem, VStack } from '@chakra-ui/react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, GeoJSON } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getDeviceEmoji, getDeviceHardwareEmoji, isDeviceActive } from '../../utils/deviceHelpers';

// Configuración de waypoints (solo los seleccionados)
const waypointConfig = [
  { file: 'Aeropuertos.geojson', emoji: '✈️', nombre: 'Aeropuerto', color: '#3498db', path: 'waypoints' },
  { file: 'Agroturismos.geojson', emoji: '🌾', nombre: 'Agroturismo', color: '#27ae60', path: 'waypoints' },
  { file: 'EstacionesBus.geojson', emoji: '🚌', nombre: 'Estación de Bus', color: '#e67e22', path: 'waypoints' },
  { file: 'EstacionesMaritimas.geojson', emoji: '⚓', nombre: 'Estación Marítima', color: '#1abc9c', path: 'waypoints' },
  { file: 'EstacionesTren.geojson', emoji: '🚆', nombre: 'Estación de Tren', color: '#9b59b6', path: 'waypoints' },
  { file: 'Hoteles.geojson', emoji: '🏨', nombre: 'Hotel', color: '#e74c3c', path: 'waypoints' },
  { file: 'Puertos.geojson', emoji: '⛵', nombre: 'Puerto', color: '#16a085', path: 'waypoints' },
  { file: 'TaxiTour.geojson', emoji: '🎯', nombre: 'Taxi Tour', color: '#f39c12', path: 'waypoints' },
  { file: 'Paradas_Palma.geojson', emoji: '🅿️', nombre: 'Parada de Taxi (Palma)', color: '#d35400', path: 'paradas', destacar: true },
  { file: 'Paradas_Marratxi.geojson', emoji: '🅿️', nombre: 'Parada de Taxi (Marratxí)', color: '#8e44ad', path: 'paradas', destacar: true }
];

// Custom icons using emoji markers
const createCustomIcon = (emoji, isActive = false) => {
  const activeIndicator = isActive ? '<div style="position: absolute; top: -2px; right: -2px; font-size: 12px;">🟢</div>' : '';
  return L.divIcon({
    html: `<div style="position: relative; font-size: 32px; text-align: center; line-height: 40px; width: 40px; height: 40px;">${emoji}${activeIndicator}</div>`,
    className: 'custom-emoji-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
};

const AdminMapa = () => {
  const { t } = useTranslation();
  const [dispositivos, setDispositivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [palmaGeoJSON, setPalmaGeoJSON] = useState(null);
  const [marratxiGeoJSON, setMarratxiGeoJSON] = useState(null);
  const [waypointsGeoJSON, setWaypointsGeoJSON] = useState([]);
  
  // Estados para logs y métricas
  const [logs, setLogs] = useState([]);
  const [metrics, setMetrics] = useState({
    latency: [],
    concurrent_devices: [],
    requests_per_minute: []
  });
  const logsRef = useRef(null);
  const mapRef = useRef(null);
  const markerRefs = useRef({});
  
  // Estado para filtros de dispositivos (por defecto todos visibles)
  const [visibleDeviceTypes, setVisibleDeviceTypes] = useState({
    conductor: true,  // 🚕
    ios: true,        // 🍎
    android: true,    // 🤖
    pc: true,         // 💻
    unknown: true     // 👤
  });
  
  // Función para toggle visibility de un tipo de dispositivo
  const toggleDeviceType = (type) => {
    setVisibleDeviceTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };
  
  // Función para determinar el tipo de dispositivo según el emoji
  const getDeviceType = (userType, platform, userAgent) => {
    if (userType === 'conductor') return 'conductor';
    
    const ua = (userAgent || '').toLowerCase();
    const plat = (platform || '').toLowerCase();
    
    if (plat.includes('ios') || plat.includes('iphone') || plat.includes('ipad') || 
        ua.includes('iphone') || ua.includes('ipad') || ua.includes('macintosh') || ua.includes('mac os')) {
      return 'ios';
    }
    
    if (plat.includes('android') || ua.includes('android')) {
      return 'android';
    }
    
    if (plat.includes('win') || plat.includes('linux') || 
        ua.includes('windows') || ua.includes('linux') || 
        (!ua.includes('mobile') && (ua.includes('mozilla') || ua.includes('chrome')))) {
      return 'pc';
    }
    
    return 'unknown';
  };
  
  // Centro del mapa en Palma de Mallorca
  const palmaDeMallorca = [39.5696, 2.6502];
  
  // Función para centrar el mapa en un dispositivo y abrir su popup
  const centerMapOnDevice = (lat, lng, dispositivoId) => {
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], 15);
    }
    
    // Abrir el popup del marcador
    if (dispositivoId && markerRefs.current[dispositivoId]) {
      setTimeout(() => {
        markerRefs.current[dispositivoId].openPopup();
      }, 300); // Pequeño delay para que el mapa se centre primero
    }
  };

  // Calcular dispositivos activos (últimos 25 minutos) con filtros aplicados
  const getActiveDevicesCount = () => {
    const fiveMinutesAgo = new Date(Date.now() - 25 * 60 * 1000);
    return dispositivos.filter(d => {
      if (!d.last_seen) return false;
      const lastSeen = new Date(d.last_seen);
      if (lastSeen < fiveMinutesAgo) return false;
      
      // Aplicar filtro de visibilidad por tipo
      const deviceType = getDeviceType(d.user_type, d.platform, d.user_agent);
      return visibleDeviceTypes[deviceType];
    }).length;
  };
  
  const activeDevicesCount = getActiveDevicesCount();

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      // Cargar el GeoJSON de Palma
      try {
        const response = await fetch('/data/geojson/limites/Poly_Palma.geojson');
        const data = await response.json();
        if (isMounted) {
          setPalmaGeoJSON(data);
        }
      } catch (err) {
        console.error('Error al cargar GeoJSON de Palma:', err);
      }
      
      // Cargar el GeoJSON de Marratxí
      try {
        const response = await fetch('/data/geojson/limites/Poly_Marratxi.geojson');
        const data = await response.json();
        if (isMounted) {
          setMarratxiGeoJSON(data);
        }
      } catch (err) {
        console.error('Error al cargar GeoJSON de Marratxí:', err);
      }
      
      // Cargar todos los waypoints
      const waypointsData = [];
      for (const config of waypointConfig) {
        try {
          const path = config.path || 'waypoints';
          const response = await fetch(`/data/geojson/${path}/${config.file}`);
          const data = await response.json();
          waypointsData.push({ ...config, data });
        } catch (err) {
          console.error(`Error al cargar waypoint ${config.file}:`, err);
        }
      }
      if (isMounted) {
        setWaypointsGeoJSON(waypointsData);
      }
      
      // Cargar los dispositivos
      await fetchDispositivos(true);
    };
    
    if (isMounted) {
      loadData();
    }
    
    // Auto-refresco cada minuto
    const intervalId = setInterval(() => {
      if (isMounted) {
        console.log('[AdminMapa] Auto-refrescando mapa...');
        fetchDispositivos();
      }
    }, 60000); // 60000 ms = 1 minuto
    
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const fetchDispositivos = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/dispositivos/mapa/todos`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Error al cargar dispositivos: ${response.status}`);
      }
      
      const data = await response.json();
      setDispositivos(data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error al cargar dispositivos:', err);
      setError('Error al cargar los dispositivos del mapa');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const fetchLogs = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/admin/geolocalizacion/logs?lineas=50`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Error al cargar logs:', err);
    }
  };
  
  const fetchMetrics = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      
      const response = await fetch(`${baseURL}/api/admin/geolocalizacion/metrics?hours=24`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (err) {
      console.error('Error al cargar métricas:', err);
    }
  };
  
  // Cargar logs y métricas periódicamente
  useEffect(() => {
    // Carga inicial
    fetchLogs();
    fetchMetrics();
    
    // Actualizar cada 30 segundos
    const interval = setInterval(() => {
      fetchLogs();
      fetchMetrics();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const formatUserData = (dispositivo) => {
    const { user_data, device_data, user_type, platform, user_agent } = dispositivo;
    const hardwareEmoji = getDeviceHardwareEmoji(platform, user_agent);
    
    return (
      <div style={{ minWidth: '300px', maxWidth: '400px' }}>
        <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '2px solid #e2e8f0' }}>
          <strong style={{ fontSize: '16px' }}>
            {user_type === 'cliente' ? '👤 Cliente' : '🚕 Conductor'}
          </strong>
        </div>
        
        {/* Datos del Usuario */}
        <div style={{ marginBottom: '15px' }}>
          <div><strong>Nombre:</strong> {user_data.nombre} {user_data.apellidos}</div>
          <div><strong>Email:</strong> {user_data.email}</div>
          <div><strong>Teléfono:</strong> {user_data.telefono || 'N/A'}</div>
          {user_type === 'conductor' && (
            <>
              <div><strong>Matrícula:</strong> {user_data.matricula_vehiculo || 'N/A'}</div>
              <div><strong>Licencia:</strong> {user_data.licencia_conducir || 'N/A'}</div>
              <div><strong>Tipo Ecotaxi:</strong> {user_data.tipo_ecotaxi || 'N/A'}</div>
            </>
          )}
          <div><strong>Habilitado:</strong> {user_data.habilitado ? '✅ Sí' : '❌ No'}</div>
        </div>
        
        {/* Datos del Dispositivo */}
        <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '2px solid #e2e8f0' }}>
          <strong style={{ fontSize: '14px' }}>{hardwareEmoji} Dispositivo</strong>
          <div style={{ marginTop: '5px', fontSize: '12px' }}>
            <div><strong>ID:</strong> {device_data.custom_device_id}</div>
            <div><strong>Plataforma:</strong> {device_data.platform || 'N/A'}</div>
            <div><strong>IP:</strong> {device_data.ip_address || 'N/A'}</div>
            <div><strong>Precisión:</strong> {device_data.location_accuracy ? `${device_data.location_accuracy}m` : 'N/A'}</div>
            <div><strong>Accesos:</strong> {device_data.access_count}</div>
            <div><strong>Última vez:</strong> {new Date(device_data.last_seen).toLocaleString('es-ES')}</div>
            <div><strong>Ubicación:</strong> {dispositivo.address || 'N/A'}</div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }
  
  // Preparar datos para gráficas (últimas 24 horas con intervalos de 15 minutos = 96 puntos)
  // Si no hay datos, generar datos de ejemplo con 0s para mostrar la estructura
  const now = new Date();
  const generateEmptyData = (count = 96) => {
    return Array.from({ length: count }, (_, i) => {
      const time = new Date(now.getTime() - (count - i - 1) * 15 * 60 * 1000);
      return {
        time: time.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        timestamp: time.toISOString()
      };
    });
  };
  
  const latencyData = metrics.latency && metrics.latency.length > 0
    ? metrics.latency.map(point => ({
        time: new Date(point.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        latencia: point.value || 0
      }))
    : generateEmptyData().map(d => ({ ...d, latencia: 0 }));
  
  const concurrentData = metrics.concurrent_devices && metrics.concurrent_devices.length > 0
    ? metrics.concurrent_devices.map(point => ({
        time: new Date(point.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        dispositivos: point.value || 0
      }))
    : generateEmptyData().map(d => ({ ...d, dispositivos: 0 }));
  
  const rpmData = metrics.requests_per_minute && metrics.requests_per_minute.length > 0
    ? metrics.requests_per_minute.map(point => ({
        time: new Date(point.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        rpm: point.value || 0
      }))
    : generateEmptyData().map(d => ({ ...d, rpm: 0 }));
  
  console.log('[AdminMapa] Datos de gráficas:', { 
    latencyPoints: latencyData.length, 
    concurrentPoints: concurrentData.length, 
    rpmPoints: rpmData.length,
    sampleLatency: latencyData[0],
    sampleConcurrent: concurrentData[0],
    sampleRpm: rpmData[0]
  });

  return (
    <VStack spacing={4} align="stretch" p={4} className="admin-page-container">
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center" className="admin-page-header">
        <Text fontSize="xl" fontWeight="bold" className="admin-page-title">
          {t('admin_mapa_titulo')} - {dispositivos.length} dispositivos
        </Text>
        <Flex alignItems="center" gap={3}>
          {lastUpdate && (
            <Text fontSize="sm" color="gray.600">
              Última actualización: {lastUpdate.toLocaleTimeString('es-ES')}
            </Text>
          )}
          <Button
            onClick={fetchDispositivos}
            colorScheme="blue"
            size="sm"
            title="Refrescar mapa"
            isLoading={refreshing}
            loadingText="Refrescando..."
            className="admin-button-primary"
          >
            🔄 Refrescar
          </Button>
        </Flex>
      </Flex>
      
      {/* Grid principal: Logs (33%) + Mapa (66%) */}
      <Grid templateColumns="33% 66%" gap={4} height="600px">
        {/* Columna de Dispositivos Activos + Logs */}
        <GridItem key="sidebar-left" display="flex" flexDirection="column" gap={4}>
          {/* Lista de Dispositivos Activos */}
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden" flex="1" display="flex" flexDirection="column" variant="outline" borderColor="gray.700">
            <Box bg="green.500" color="white" p={2} fontWeight="bold">
              🟢 Dispositivos Activos ({activeDevicesCount})
            </Box>
            <VStack
              align="stretch"
              spacing={0}
              flex="1"
              overflowY="auto"
              bg="white"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {dispositivos
                .filter(d => {
                  if (!d.last_seen) return false;
                  const fiveMinutesAgo = new Date(Date.now() - 25 * 60 * 1000);
                  const lastSeen = new Date(d.last_seen);
                  return lastSeen >= fiveMinutesAgo;
                })
                .filter(d => {
                  // Aplicar filtro de visibilidad por tipo
                  const deviceType = getDeviceType(d.user_type, d.platform, d.user_agent);
                  return visibleDeviceTypes[deviceType];
                })
                .map(dispositivo => {
                  const deviceEmoji = getDeviceEmoji(dispositivo.user_type, dispositivo.platform, dispositivo.user_agent);
                  const lastSeenDate = new Date(dispositivo.last_seen);
                  const minutesAgo = Math.floor((Date.now() - lastSeenDate.getTime()) / 60000);
                  
                  return (
                    <Box
                      key={dispositivo.id}
                      p={2}
                      borderBottomWidth="1px"
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => centerMapOnDevice(dispositivo.latitude, dispositivo.longitude, dispositivo.dispositivo_id)}
                    >
                      <Flex align="center" gap={2}>
                        <Text fontSize="xl">{deviceEmoji}</Text>
                        <Box flex="1">
                          <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>
                            {dispositivo.user_name || dispositivo.user_email || 'Usuario desconocido'}
                          </Text>
                          <Text fontSize="xs" color="gray.600" noOfLines={1}>
                            {dispositivo.address || `${dispositivo.latitude?.toFixed(4)}, ${dispositivo.longitude?.toFixed(4)}`}
                          </Text>
                          <Text fontSize="xs" color="green.600">
                            🟢 {minutesAgo === 0 ? 'Ahora' : `${minutesAgo}m`}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  );
                })}
              {activeDevicesCount === 0 && (
                <Box p={4} textAlign="center" color="gray.500">
                  <Text fontSize="sm">No hay dispositivos activos en este momento</Text>
                </Box>
              )}
            </VStack>
            
            {/* Leyenda de emojis - Filtros clickeables */}
            <Box 
              p={2} 
              bg="white" 
              borderTopWidth="1px" 
              borderColor="gray.200"
            >
              <Text fontSize="xs" fontWeight="bold" color="gray.800" mb={1}>
                Filtros (click para mostrar/ocultar):
              </Text>
              <Flex wrap="wrap" gap={2} fontSize="xs">
                <Box
                  cursor="pointer" 
                  onClick={() => toggleDeviceType('conductor')}
                  opacity={visibleDeviceTypes.conductor ? 1 : 0.4}
                  textDecoration={visibleDeviceTypes.conductor ? 'none' : 'line-through'}
                  color="gray.800"
                  fontWeight="medium"
                  _hover={{ bg: 'gray.100' }}
                  px={2}
                  py={1}
                  borderRadius="md"
                  transition="all 0.2s"
                >
                  🚕 Conductor
                </Box>
                <Box
                  cursor="pointer" 
                  onClick={() => toggleDeviceType('ios')}
                  opacity={visibleDeviceTypes.ios ? 1 : 0.4}
                  textDecoration={visibleDeviceTypes.ios ? 'none' : 'line-through'}
                  color="gray.800"
                  fontWeight="medium"
                  _hover={{ bg: 'gray.100' }}
                  px={2}
                  py={1}
                  borderRadius="md"
                  transition="all 0.2s"
                >
                  🍎 iOS
                </Box>
                <Box
                  cursor="pointer" 
                  onClick={() => toggleDeviceType('android')}
                  opacity={visibleDeviceTypes.android ? 1 : 0.4}
                  textDecoration={visibleDeviceTypes.android ? 'none' : 'line-through'}
                  color="gray.800"
                  fontWeight="medium"
                  _hover={{ bg: 'gray.100' }}
                  px={2}
                  py={1}
                  borderRadius="md"
                  transition="all 0.2s"
                >
                  🤖 Android
                </Box>
                <Box
                  cursor="pointer" 
                  onClick={() => toggleDeviceType('pc')}
                  opacity={visibleDeviceTypes.pc ? 1 : 0.4}
                  textDecoration={visibleDeviceTypes.pc ? 'none' : 'line-through'}
                  color="gray.800"
                  fontWeight="medium"
                  _hover={{ bg: 'gray.100' }}
                  px={2}
                  py={1}
                  borderRadius="md"
                  transition="all 0.2s"
                >
                  💻 PC
                </Box>
                <Box
                  cursor="pointer" 
                  onClick={() => toggleDeviceType('unknown')}
                  opacity={visibleDeviceTypes.unknown ? 1 : 0.4}
                  textDecoration={visibleDeviceTypes.unknown ? 'none' : 'line-through'}
                  color="gray.800"
                  fontWeight="medium"
                  _hover={{ bg: 'gray.100' }}
                  px={2}
                  py={1}
                  borderRadius="md"
                  transition="all 0.2s"
                >
                  👤 Desconocido
                </Box>
              </Flex>
            </Box>
          </Box>
          
          {/* Logs de Geolocalización */}
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden" height="280px" display="flex" flexDirection="column" variant="outline" borderColor="gray.700">
            <Box bg="blue.500" color="white" p={2} fontWeight="bold">
              📋 Logs de Geolocalización
            </Box>
            <Textarea
              ref={logsRef}
              value={logs.map(log => {
                const prefix = log.is_error ? '❌ ERROR: ' : '✅ ';
                return `[${log.timestamp}] ${prefix}${log.message}`;
              }).join('\n')}
              readOnly
              placeholder="Los logs aparecerán aquí en tiempo real..."
              fontFamily="monospace"
              fontSize="xs"
              bg="gray.50"
              color="gray.800"
              borderColor="gray.300"
              flex="1"
              resize="none"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            />
          </Box>
        </GridItem>
        
        {/* Columna del Mapa */}
        <GridItem key="map-container">
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden" height="100%">
            <MapContainer 
              center={palmaDeMallorca} 
              zoom={12} 
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Polígono del término municipal de Palma */}
              {palmaGeoJSON && (
                <GeoJSON 
                  data={palmaGeoJSON}
                  style={{
                    fillColor: '#99d9ea',
                    fillOpacity: 0.2,
                    color: '#2980b9',
                    weight: 2,
                    opacity: 0.8
                  }}
                />
              )}
              
              {/* Polígono del término municipal de Marratxí */}
              {marratxiGeoJSON && (
                <GeoJSON 
                  data={marratxiGeoJSON}
                  style={{
                    fillColor: '#90ee90',
                    fillOpacity: 0.2,
                    color: '#27ae60',
                    weight: 2,
                    opacity: 0.8
                  }}
                />
              )}
              
              {/* Waypoints (aeropuertos, hoteles, puertos, etc.) */}
              {waypointsGeoJSON.map((waypoint, index) => (
                <GeoJSON 
                  key={`waypoint-${index}`}
                  data={waypoint.data}
                  pointToLayer={(feature, latlng) => {
                    const esParadaTaxi = waypoint.destacar === true;
                    const fontSize = esParadaTaxi ? 18 : 16;
                    const opacity = esParadaTaxi ? 0.6 : 0.5;
                    const iconSize = esParadaTaxi ? 22 : 20;
                    const zIndexOffset = esParadaTaxi ? 1000 : 0;
                    
                    const icon = L.divIcon({
                      html: `<div style="font-size: ${fontSize}px; opacity: ${opacity}; text-shadow: 0 0 3px white;">${waypoint.emoji}</div>`,
                      className: 'waypoint-icon',
                      iconSize: [iconSize, iconSize],
                      iconAnchor: [iconSize/2, iconSize/2],
                      popupAnchor: [0, -(iconSize/2)]
                    });
                    return L.marker(latlng, { icon, zIndexOffset });
                  }}
                  onEachFeature={(feature, layer) => {
                    if (feature.properties) {
                      const props = feature.properties;
                      let tooltipContent = `<div style="max-width: 350px;">`;
                      
                      const nombrePunto = props.Nombre || props.nombre || props.name || props.Name || 
                                         props.title || props.Title || props.titulo ||
                                         props.label || props.Label || props.id || props.ID;
                      
                      if (nombrePunto) {
                        tooltipContent += `<strong style="font-size: 14px;">${waypoint.emoji} ${nombrePunto}</strong><br/>`;
                      } else {
                        tooltipContent += `<strong style="font-size: 14px;">${waypoint.emoji} ${waypoint.nombre}</strong><br/>`;
                      }
                      
                      const tipoFeature = props.Type_of_feature || props.type || props.Type || props.tipo;
                      if (tipoFeature) {
                        tooltipContent += `<small style="color: ${waypoint.color};">• ${tipoFeature}</small><br/>`;
                      }
                      
                      const municipio = props.Municipio || props.municipio || props.municipality;
                      if (municipio) {
                        tooltipContent += `<small>🏛️ ${municipio}</small>`;
                      }
                      
                      const cp = props.cp || props.CP || props.postal_code || props.zipcode;
                      if (cp) {
                        tooltipContent += `<small> (CP: ${cp})</small>`;
                      }
                      if (municipio || cp) {
                        tooltipContent += `<br/>`;
                      }
                      
                      const direccion = props.Direccion || props.direccion || props.address || 
                                       props.Address || props.location || props.ubicacion;
                      if (direccion) {
                        tooltipContent += `<small>📍 ${direccion}</small><br/>`;
                      }
                      
                      const telefono = props.Telefono || props.telefono || props.phone || 
                                      props.Phone || props.tel || props.contact;
                      if (telefono) {
                        tooltipContent += `<small>📞 ${telefono}</small><br/>`;
                      }
                      
                      const email = props.Email || props.email || props.mail;
                      if (email) {
                        tooltipContent += `<small>📧 ${email}</small><br/>`;
                      }
                      
                      const web = props.Url || props.url || props.URL || props.website || 
                                 props.web || props.link;
                      if (web) {
                        tooltipContent += `<small>🌐 ${web}</small><br/>`;
                      }
                      
                      const descripcion = props.Descripcion || props.descripcion || props.description || 
                                         props.Description || props.desc || props.info || props.details;
                      if (descripcion) {
                        tooltipContent += `<small style="font-style: italic;">${descripcion}</small><br/>`;
                      }
                      
                      const funcion = props.Funcion || props.funcion || props.function || 
                                     props.ServicioEnCurso || props.servicio;
                      if (funcion) {
                        tooltipContent += `<small>⚙️ ${funcion}</small><br/>`;
                      }
                      
                      const propietario = props.Propietario || props.propietario || props.owner;
                      if (propietario) {
                        tooltipContent += `<small>👤 ${propietario}</small><br/>`;
                      }
                      
                      const licencia = props.Licencia || props.licencia || props.license;
                      if (licencia) {
                        tooltipContent += `<small>📄 Lic: ${licencia}</small><br/>`;
                      }
                      
                      tooltipContent += `</div>`;
                      
                      layer.bindTooltip(tooltipContent, { 
                        direction: 'top',
                        offset: [0, -8],
                        opacity: 0.95,
                        className: 'waypoint-tooltip'
                      });
                      
                      layer.bindPopup(tooltipContent.replace('max-width: 350px', 'min-width: 280px; max-width: 400px'));
                    }
                  }}
                />
              ))}
              
              {/* Marcadores de dispositivos */}
              {dispositivos
                .filter(d => {
                  // Aplicar filtro de visibilidad por tipo
                  const deviceType = getDeviceType(d.user_type, d.platform, d.user_agent);
                  return visibleDeviceTypes[deviceType];
                })
                .map((dispositivo) => {
                const isActive = isDeviceActive(dispositivo.last_seen, 20);
                const deviceEmoji = getDeviceEmoji(dispositivo.user_type, dispositivo.platform, dispositivo.user_agent);
                const icon = createCustomIcon(deviceEmoji, isActive);
                
                return (
                  <Marker
                    key={dispositivo.dispositivo_id}
                    position={[dispositivo.latitude, dispositivo.longitude]}
                    icon={icon}
                    zIndexOffset={2000}
                    ref={(ref) => {
                      if (ref) {
                        markerRefs.current[dispositivo.dispositivo_id] = ref;
                      }
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -30]} opacity={0.9}>
                      <div style={{ textAlign: 'center' }}>
                        <strong>{dispositivo.user_name}</strong>
                        <br />
                        <small>{deviceEmoji} {dispositivo.user_type === 'cliente' ? 'Cliente' : 'Conductor'}</small>
                        {isActive && <><br /><small>🟢 Activo</small></>}
                      </div>
                    </Tooltip>
                    
                    <Popup maxWidth={450}>
                      {formatUserData(dispositivo)}
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </Box>
        </GridItem>
      </Grid>
      
      {/* Título de monitorización */}
      <Box mt={4} mb={2}>
        <Text fontSize="lg" fontWeight="bold" color="gray.700">
          {t('admin_mapa_monitorizacion_titulo')}
        </Text>
        <Text fontSize="sm" color="orange.600" fontStyle="italic">
          {t('admin_mapa_monitorizacion_subtitulo')}
        </Text>
      </Box>
      
      {/* Gráficas de métricas - 3 columnas */}
      <Grid templateColumns="repeat(3, 1fr)" gap={4}>
        {/* Gráfica 1: Latencia */}
        <GridItem key="graph-latency">
          <Box borderWidth="1px" borderRadius="lg" p={3} bg="white">
            <Text fontSize="sm" fontWeight="bold" mb={2}>📊 Latencia (ms)</Text>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={latencyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="time" 
                  fontSize={9} 
                  angle={-45} 
                  textAnchor="end" 
                  height={50}
                  stroke="#666"
                />
                <YAxis 
                  fontSize={10} 
                  stroke="#666"
                  domain={[0, 250]}
                />
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <ReferenceLine 
                  y={200} 
                  stroke="#e53e3e" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  label={{ 
                    value: 'Umbral: 200ms', 
                    position: 'insideTopRight', 
                    fontSize: 10,
                    fill: '#e53e3e',
                    fontWeight: 'bold'
                  }} 
                />
                <Line 
                  type="monotone" 
                  dataKey="latencia" 
                  stroke="#3182ce" 
                  strokeWidth={2.5} 
                  dot={{ r: 4, fill: '#3182ce' }} 
                  activeDot={{ r: 6 }}
                  name="Latencia (ms)" 
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </GridItem>
        
        {/* Gráfica 2: Dispositivos Concurrentes */}
        <GridItem key="graph-concurrent">
          <Box borderWidth="1px" borderRadius="lg" p={3} bg="white">
            <Text fontSize="sm" fontWeight="bold" mb={2}>👥 Dispositivos Concurrentes</Text>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={concurrentData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="time" 
                  fontSize={9} 
                  angle={-45} 
                  textAnchor="end" 
                  height={50}
                  stroke="#666"
                />
                <YAxis 
                  fontSize={10} 
                  stroke="#666"
                  domain={[0, 600]}
                />
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <ReferenceLine 
                  y={500} 
                  stroke="#e53e3e" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  label={{ 
                    value: 'Umbral: 500', 
                    position: 'insideTopRight', 
                    fontSize: 10,
                    fill: '#e53e3e',
                    fontWeight: 'bold'
                  }} 
                />
                <Line 
                  type="monotone" 
                  dataKey="dispositivos" 
                  stroke="#38a169" 
                  strokeWidth={2.5} 
                  dot={{ r: 4, fill: '#38a169' }} 
                  activeDot={{ r: 6 }}
                  name="Dispositivos" 
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </GridItem>
        
        {/* Gráfica 3: Requests por Minuto */}
        <GridItem key="graph-rpm">
          <Box borderWidth="1px" borderRadius="lg" p={3} bg="white">
            <Text fontSize="sm" fontWeight="bold" mb={2}>⚡ Requests/minuto</Text>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={rpmData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="time" 
                  fontSize={9} 
                  angle={-45} 
                  textAnchor="end" 
                  height={50}
                  stroke="#666"
                />
                <YAxis 
                  fontSize={10} 
                  stroke="#666"
                  domain={[0, 250]}
                />
                <RechartsTooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <ReferenceLine 
                  y={200} 
                  stroke="#e53e3e" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  label={{ 
                    value: 'Umbral: 200', 
                    position: 'insideTopRight', 
                    fontSize: 10,
                    fill: '#e53e3e',
                    fontWeight: 'bold'
                  }} 
                />
                <Line 
                  type="monotone" 
                  dataKey="rpm" 
                  stroke="#d69e2e" 
                  strokeWidth={2.5} 
                  dot={{ r: 4, fill: '#d69e2e' }} 
                  activeDot={{ r: 6 }}
                  name="Req/min" 
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </GridItem>
      </Grid>
    </VStack>
  );
};

export default AdminMapa;